<template>
  <div class="container-1 relative z-2 w-full h-full md:flex items-center justify-center gap-12">
    <div class="w-full md:w-6/12">
      <div class="mt-24 md:mt-0 font-semibold uppercase text-ellie-blue text-[3rem] md:text-[6vw] leading-[1.2] -tracking-[2px] whitespace-pre-line w-full">
        <div class="h-full flex flex-col md:flex-row items-center justify-start relative">
          <h1 ref="splitTextContainer" aria-label="Encounters That Transcend Dimensions" class="hero-text overflow-hidden"/>
          <div class="graphic-2 absolute w-[15%] h-[15%] bg-ellie-purple animated-container-3 left-[45%] top-[30%] opacity-0"/>
        </div>
      </div>
    </div>
    <div class="md:w-6/12 h-full relative -mt-6 md:mt-0">
      <div id="masked-vid-container" data-aos="fade-left">
        <div id="reel" class="aspect-[4/5] w-full relative block h-full overflow-hidden">
          <video id="vid-reel" src="/video/5s-reel.mp4" muted autoplay loop playsinline class="absolute h-full w-full top-0 object-cover"/>
          <div class="absolute z-10 w-full flex items-center justify-center gap-[1vw] h-full overflow-x-hidden">
            <div class="reel-button-animation bg-ellie-blue text-white opacity-75 hover:opacity-100 hover:scale-110 transition-opacity transition-transform duration-500 w-12 h-12 rounded-full flex items-center justify-center overflow-hidden" @mouseenter="toggleHover(true)" @mouseleave="toggleHover(true, 'scroll-down')" @click="watchReel()">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="play-icon size-6">
                <path fill-rule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="absolute bottom-0 h-20 w-full"/>
</template>

<script setup>
const { $gsap, $imagesLoaded, $anime } = useNuxtApp();
const emits = defineEmits(['onLoaded', 'onPlayVideo']);
const mainStore = useMainStore();

const text = ref("Encounters That Transcend Dimensions");
const splitTextContainer = ref(null);

onMounted(() => {
  splitTextContainer.value.innerHTML = text.value
    .split(" ")
    .map(
      (word) =>
        `<span class="word">${word
          .split("")
          .map((char) => `<span class="text-hero">${char}</span>`)
          .join("")}</span>`
    )
    .join(" "); // Add real spaces to maintain word breaks
  nextTick(() => {
    emits('onLoaded');
    animateContent()
    animatedClipPath();
  });
});

const toggleHover = (isHover, type = 'default') => {
  const payload = { isHover, type };
  mainStore.toggleTrackingMouse(payload);
};

const animateContent = () => {
  $anime({
    targets: '.text-hero',
    opacity: [0, 1],
    translateX: ['20%', 0],
    translateY: ['40%', 0],
    rotate: ['5deg', 0],
    easing: "easeOutExpo",
    duration: 700,
    delay: $anime.stagger(50),
    complete: () => {
      animatedGraphics()
    }
  });
};

const animatedGraphics = () => {
  $anime({
    targets: '.graphic-2',
    opacity: [0, 1],
    translateX: ['20%', 0],
    translateY: ['40%', 0],
    rotate: ['5deg', 0],
    easing: "easeOutExpo",
    duration: 700
  });
};

const animatedClipPath = () => {
  const tl = $anime.timeline({
    loop: true,
    easing: 'easeOutCubic',
    targets: '.animated-container-3',
    duration: 2000,
  });

  tl
    .add({
      clipPath: 'polygon( 0% 41.522%,0% 81.804%,0% 81.804%,0.003% 81.884%,0.01% 81.961%,0.022% 82.036%,0.038% 82.105%,0.059% 82.17%,0.083% 82.229%,0.111% 82.282%,0.142% 82.327%,0.176% 82.364%,0.212% 82.392%,28.495% 99.974%,28.495% 99.974%,28.509% 99.982%,28.523% 99.988%,28.538% 99.994%,28.552% 99.997%,28.567% 99.999%,28.581% 100%,28.596% 99.999%,28.611% 99.997%,28.625% 99.993%,28.64% 99.988%,99.76% 64.717%,99.76% 64.717%,99.8% 64.694%,99.838% 64.661%,99.873% 64.617%,99.905% 64.565%,99.932% 64.504%,99.956% 64.436%,99.974% 64.363%,99.988% 64.283%,99.997% 64.2%,100% 64.113%,99.983% 0.614%,99.983% 0.614%,99.978% 0.514%,99.967% 0.42%,99.949% 0.332%,99.924% 0.252%,99.894% 0.18%,99.859% 0.119%,99.82% 0.069%,99.778% 0.031%,99.732% 0.008%,99.683% 0%,31.51% 0%,31.51% 0%,31.493% 0.001%,31.476% 0.004%,31.46% 0.009%,31.443% 0.016%,31.427% 0.024%,31.411% 0.035%,31.395% 0.048%,31.379% 0.062%,31.364% 0.078%,31.35% 0.097%,0.138% 41.003%,0.138% 41.003%,0.114% 41.039%,0.091% 41.08%,0.07% 41.125%,0.052% 41.174%,0.037% 41.226%,0.024% 41.281%,0.014% 41.338%,0.006% 41.398%,0.002% 41.459%,0% 41.522% )',
    })
    .add({
      clipPath: 'polygon( 0% 18.107%,0% 64.351%,0% 64.351%,0.002% 64.417%,0.008% 64.48%,0.017% 64.54%,0.03% 64.598%,0.045% 64.652%,0.064% 64.701%,0.086% 64.746%,0.11% 64.785%,0.136% 64.818%,0.165% 64.845%,37.104% 94.122%,37.104% 94.122%,37.112% 94.128%,37.12% 94.133%,37.128% 94.138%,37.136% 94.142%,37.144% 94.146%,37.152% 94.149%,37.16% 94.152%,37.168% 94.154%,37.177% 94.155%,37.185% 94.156%,99.703% 99.999%,99.703% 99.999%,99.746% 99.996%,99.787% 99.979%,99.825% 99.948%,99.86% 99.906%,99.892% 99.853%,99.919% 99.791%,99.941% 99.721%,99.957% 99.643%,99.968% 99.56%,99.971% 99.471%,100% 41.414%,100% 41.414%,99.999% 41.368%,99.996% 41.324%,99.992% 41.28%,99.985% 41.237%,99.977% 41.195%,99.967% 41.155%,99.956% 41.117%,99.943% 41.081%,99.928% 41.047%,99.912% 41.015%,77.228% 0.131%,77.228% 0.131%,77.211% 0.103%,77.193% 0.079%,77.175% 0.057%,77.155% 0.039%,77.135% 0.024%,77.115% 0.013%,77.094% 0.005%,77.073% 0.001%,77.052% 0%,77.03% 0.003%,0.228% 17.58%,0.228% 17.58%,0.19% 17.595%,0.155% 17.621%,0.122% 17.656%,0.092% 17.701%,0.065% 17.754%,0.043% 17.814%,0.025% 17.88%,0.011% 17.952%,0.003% 18.028%,0% 18.107% )',
    })
    .add({
      clipPath: 'polygon( 0% 11.461%,0% 35.719%,0% 35.719%,0% 35.737%,0.001% 35.755%,0.003% 35.773%,0.005% 35.79%,0.008% 35.807%,0.011% 35.824%,0.015% 35.84%,0.02% 35.855%,0.025% 35.869%,0.031% 35.883%,29.088% 99.927%,29.088% 99.927%,29.096% 99.942%,29.103% 99.955%,29.112% 99.967%,29.12% 99.977%,29.129% 99.985%,29.138% 99.991%,29.148% 99.996%,29.158% 99.999%,29.167% 100%,29.177% 99.999%,99.868% 88.259%,99.868% 88.259%,99.885% 88.253%,99.901% 88.242%,99.916% 88.227%,99.929% 88.207%,99.941% 88.184%,99.951% 88.157%,99.959% 88.127%,99.965% 88.094%,99.969% 88.06%,99.97% 88.024%,100% 23.708%,100% 23.708%,99.999% 23.676%,99.996% 23.646%,99.992% 23.617%,99.985% 23.59%,99.977% 23.565%,99.968% 23.542%,99.957% 23.522%,99.946% 23.505%,99.933% 23.491%,99.919% 23.48%,61.045% 0.009%,61.045% 0.009%,61.041% 0.007%,61.037% 0.005%,61.033% 0.003%,61.029% 0.002%,61.025% 0.001%,61.021% 0%,61.017% 0%,61.013% 0%,61.008% 0%,61.004% 0.001%,0.102% 11.225%,0.102% 11.225%,0.085% 11.231%,0.069% 11.242%,0.054% 11.257%,0.041% 11.277%,0.029% 11.301%,0.019% 11.328%,0.011% 11.358%,0.005% 11.39%,0.001% 11.425%,0% 11.461% )',
    })
    .add({
      clipPath: 'polygon( 0% 31.687%,2.695% 86.99%,2.695% 86.99%,2.697% 87.077%,2.705% 87.162%,2.716% 87.242%,2.732% 87.319%,2.753% 87.389%,2.776% 87.454%,2.803% 87.511%,2.834% 87.56%,2.867% 87.6%,2.903% 87.631%,29.927% 99.815%,29.927% 99.815%,29.94% 99.824%,29.954% 99.831%,29.969% 99.837%,29.983% 99.841%,29.997% 99.843%,30.011% 99.844%,30.026% 99.843%,30.04% 99.84%,30.055% 99.836%,30.069% 99.83%,99.74% 73.98%,99.74% 73.98%,99.78% 73.955%,99.818% 73.918%,99.852% 73.87%,99.882% 73.813%,99.909% 73.747%,99.932% 73.674%,99.951% 73.593%,99.964% 73.507%,99.973% 73.417%,99.976% 73.322%,92.582% 12.755%,92.582% 12.755%,92.578% 12.647%,92.566% 12.544%,92.548% 12.449%,92.524% 12.361%,92.495% 12.283%,92.461% 12.216%,92.423% 12.162%,92.381% 12.121%,92.336% 12.096%,92.289% 12.087%,13.971% 0%,13.971% 0%,13.954% 0.001%,13.938% 0.004%,13.921% 0.01%,13.905% 0.017%,13.889% 0.026%,13.873% 0.038%,13.858% 0.052%,13.843% 0.067%,13.828% 0.085%,13.814% 0.105%,0.136% 31.123%,0.136% 31.123%,0.111% 31.162%,0.089% 31.207%,0.069% 31.256%,0.051% 31.309%,0.036% 31.365%,0.023% 31.425%,0.013% 31.488%,0.006% 31.552%,0.002% 31.619%,0% 31.687% );',
    })
    .add({
      clipPath: 'polygon( 0% 41.522%,0% 81.804%,0% 81.804%,0.003% 81.884%,0.01% 81.961%,0.022% 82.036%,0.038% 82.105%,0.059% 82.17%,0.083% 82.229%,0.111% 82.282%,0.142% 82.327%,0.176% 82.364%,0.212% 82.392%,28.495% 99.974%,28.495% 99.974%,28.509% 99.982%,28.523% 99.988%,28.538% 99.994%,28.552% 99.997%,28.567% 99.999%,28.581% 100%,28.596% 99.999%,28.611% 99.997%,28.625% 99.993%,28.64% 99.988%,99.76% 64.717%,99.76% 64.717%,99.8% 64.694%,99.838% 64.661%,99.873% 64.617%,99.905% 64.565%,99.932% 64.504%,99.956% 64.436%,99.974% 64.363%,99.988% 64.283%,99.997% 64.2%,100% 64.113%,99.983% 0.614%,99.983% 0.614%,99.978% 0.514%,99.967% 0.42%,99.949% 0.332%,99.924% 0.252%,99.894% 0.18%,99.859% 0.119%,99.82% 0.069%,99.778% 0.031%,99.732% 0.008%,99.683% 0%,31.51% 0%,31.51% 0%,31.493% 0.001%,31.476% 0.004%,31.46% 0.009%,31.443% 0.016%,31.427% 0.024%,31.411% 0.035%,31.395% 0.048%,31.379% 0.062%,31.364% 0.078%,31.35% 0.097%,0.138% 41.003%,0.138% 41.003%,0.114% 41.039%,0.091% 41.08%,0.07% 41.125%,0.052% 41.174%,0.037% 41.226%,0.024% 41.281%,0.014% 41.338%,0.006% 41.398%,0.002% 41.459%,0% 41.522% )',
    });

  tl.play();
};

const watchReel = () => {
    emits('onPlayVideo')
  }
</script>

<style lang="scss">
.text-hero {
  display: inline-block;
  transform: translateY(40%) translateX(20%) rotate(5deg);
  opacity: 0;
}
span.word {
  @apply flex;
}
#masked-vid-container {
  width: 100%;
  height: 100%;
  background-size: 100%;
  background-position: top center;
  background-repeat: no-repeat;
  background-color: white;
  -webkit-mask-image: url(/graphics/hero-polygon.svg);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-image: url(/graphics/hero-polygon.svg);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
}
</style>